import React, {
  useState,
  useCallback,
  useMemo,
  useRef,
  useEffect,
} from "react";
import {
  Editable,
  withReact,
  use,
  useSlate,
  useSelected,
  ReactEditor,
} from "slate-react";
import { Editor, Transforms, createEditor, Node, Text } from "slate";
import { withHistory } from "slate-history";
import { ErrorBoundary } from "react-error-boundary";
import isHotkey from "is-hotkey";
import styled from "styled-components";
import {
  AlignPlugin,
  ItalicPlugin,
  StrikethroughPlugin,
  UnderlinePlugin,
  SoftBreakPlugin,
  EditablePlugins,
  ExitBreakPlugin,
  ResetBlockTypePlugin,
  pipe,
  // ListPlugin,
  // withList,
  withNodeID,
  withDeserializeHTML,
  // serializeHTMLFromNodes,
  withInlineVoid,
  isBlockAboveEmpty,
  isSelectionAtBlockStart,
  isSelectionAtBlockEnd,
  deserializeHTMLToDocumentFragment,
  getNode,
  isAncestor,
  getParent,
  getListItemEntry,
  isFirstChild,
} from "@udecode/slate-plugins";
import {
  withSignatures,
  ClausePlugin,
  TextPlugin,
  TitlePlugin,
  StatementPlugin,
  SignaturePlugin,
  VariablePlugin,
  VariablesMenu,
  StaticHeaderPlugin,
  HeaderPlugin,
  PageBreakPlugin,
  ImagePlugin,
  withImage,
  TablePlugin,
  withTable,
  DocumentFooterPlugin,
  DocumentHeaderPlugin,
  withList,
  ListPlugin,
  // BaseElementPlugin,
  // withBaseElement
  BLOCK_TABLE_WRAP,
} from "./Elements";
import {
  FontColorPlugin,
  BackgroundColorPlugin,
  FontSizePlugin,
  LineHeightPlugin,
} from "./Marks";
import { options } from "./pluginOptions";
import { getMongoObjectId } from "../../../utils";
import useOnClickOutside from "../../../hooks/useOnClickOutside";
import { serializeHTMLFromNodes } from "./editor-utils";
import { BoldPlugin } from "./plugins";
import { uploadFileV2 } from "../../../api/actions/files";
import { fetcher } from "../../../api/client";

import {
  useEditorState,
  useEditorActions,
  getParticipantsMap,
} from "../../../context/editor";
// import { withList } from './withList'

const getWithPlugins = () => {
  const plugins = getPlugins();

  return [
    withReact,
    withHistory,
    withSignatures,
    withList(options),
    withNodeID({
      idKey: "_id",
      idCreator: () => getMongoObjectId(),
    }),
    withDeserializeHTML({ plugins }),
    withInlineVoid({ plugins }),
    withImage({
      handleUpload: (file) => fetcher(uploadFileV2(file)),
    }),
    withTable(),
    // withBaseElement()
  ];
};

export const deserializeHTML = (
  html,
  { contract = {}, participants = new Map() } = {}
) => {
  const document = new DOMParser().parseFromString(html, "text/html");
  const plugins = getPlugins({ contract, participants });
  // REMOVE ALIGN PLUGIN
  plugins.shift();
  return deserializeHTMLToDocumentFragment({ plugins, element: document.body });
};

export const serializeHTML = ({ nodes, props = {}, contract = {} }) => {
  const participants = getParticipantsMap(contract.participants);
  const plugins = getPlugins({ contract, participants });

  return serializeHTMLFromNodes({
    plugins,
    nodes,
    slateProps: { ...props, contract, participants },
  });
};

export const getEditorInstance = () => {
  const withPlugins = getWithPlugins();

  return pipe(createEditor(), ...withPlugins);
};

export const getPlugins = ({
  contract,
  participants,
  showPlaceholder,
  showSignatureBorder,
  showRemoveSignature,
  currentParticipantId,
  signatureConfirmations,
  showSignatureConfirmation,
  onConfirmSignature,
  BaseElementComponent = DefaultBaseElement,
} = {}) => {
  const sharedConfig = {
    showPlaceholder,
    BaseElementComponent,
  };

  return [
    AlignPlugin(options),
    BoldPlugin(options),
    ItalicPlugin(options),
    StrikethroughPlugin(options),
    UnderlinePlugin(options),
    ListPlugin({ ...options, ...sharedConfig }),
    SoftBreakPlugin({
      rules: [
        {
          hotkey: "enter",
          query: {
            allow: [
              "text",
              "clause",
              "statement",
              "table-wrap",
              "title",
              "static-header",
              "document_header",
              "document_footer",
            ],
          },
        },
      ],
    }),
    ExitBreakPlugin({
      rules: [
        {
          hotkey: "shift+enter",
          query: {
            allow: ["text"],
          },
          defaultType: "text",
        },
        //     {
        //       hotkey: 'mod+shift+enter',
        //       before: true,
        //     },
        //     // {
        //     //   hotkey: 'enter',
        //     //   query: {
        //     //     start: true,
        //     //     end: true,
        //     //     // allow: headingTypes,
        //     //   },
        //     // },
      ],
    }),
    ClausePlugin({ ...sharedConfig, contract }),
    TextPlugin(sharedConfig),
    TitlePlugin(sharedConfig),
    StatementPlugin(sharedConfig),
    SignaturePlugin({
      ...sharedConfig,
      contract,
      participants,
      signatureConfirmations,
      currentParticipantId,
      showSignatureConfirmation,
      showSignatureBorder,
      showRemoveSignature,
      onConfirmSignature,
    }),
    VariablePlugin({ ...sharedConfig, contract, participants }),
    HeaderPlugin({ ...sharedConfig, contract }),
    StaticHeaderPlugin({ ...sharedConfig, contract }),
    PageBreakPlugin(sharedConfig),
    ImagePlugin(),
    TablePlugin(sharedConfig),
    FontColorPlugin(),
    BackgroundColorPlugin(),
    FontSizePlugin(),
    LineHeightPlugin(),
    // BaseElementPlugin({ setCurrentElement }),
    DocumentHeaderPlugin(sharedConfig),
    DocumentFooterPlugin(sharedConfig),
  ];
};

function ErrorFallback({ error }) {
  return (
    <div className="editor-canvas-html">
      <div role="alert">
        <p>Something went wrong:</p>
        <pre style={{ color: "red" }}>{error.message}</pre>
      </div>
    </div>
  );
}

const StyledEditablePlugins = styled(EditablePlugins)`
  width: 21cm;
  min-height: 29.7cm;
  display: block;
  margin: 0 auto;
  padding: 1.5cm;
  marginbottom: 0.5cm;
  boxshadow: none;
  boxsizing: border-box;
`;

export const HtmlDocumentContainer = styled.div`
  .editor-canvas-html {
    background: #fff;
    width: 21cm;
    min-height: 29.7cm;
    display: block;
    margin: 0 auto;
    padding: 1.5cm;
    margin-bottom: 0.5cm;
    box-shadow: none;
    border: 1px solid #e4e4e4;
    overflow: visible;
    box-sizing: border-box;
    font-size: 12pt;
    position: relative;
  }
`;

export function TratoFormat({
  className,
  variablesState = {},
  isEditable,
  document: doc,
  participantsMap,
  setCurrentElement,
  onAddVariable,
  showSignatureBorder,
  showRemoveSignature,
  showPlaceholder = true,
  currentParticipantId,
  showSignatureConfirmation,
  signatureConfirmations,
  onConfirmSignature,
  BaseElementComponent = DefaultBaseElement,
  defaultFontSize = "12px",
}) {
  const editor = useSlate();
  const canvasRef = useRef();
  const participants = useMemo(() => {
    if (participantsMap) return participantsMap;
    return getParticipantsMap(doc.participants);
  }, [doc.participants, participantsMap]);

  useOnClickOutside(canvasRef, (evt) => {
    setCurrentElement({});
  });

  // console.log({participants})

  const plugins = useMemo(() => {
    return getPlugins({
      contract: doc,
      participants,
      currentParticipantId,
      showSignatureConfirmation,
      showSignatureBorder,
      showRemoveSignature,
      showPlaceholder,
      signatureConfirmations,
      onConfirmSignature,
      BaseElementComponent,
    });
  }, [
    doc,
    participants,
    showPlaceholder,
    showSignatureBorder,
    showRemoveSignature,
    currentParticipantId,
    signatureConfirmations,
    showSignatureConfirmation,
    BaseElementComponent,
  ]);

  const {
    search,
    path,
    target,
    values = [],
    onKeyDownVariable,
    onAddVariable: onVariableClick,
  } = variablesState;
  useEffect(() => {
    console.log(onVariableClick);
  }, [editor]);

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      {/* <div ref={canvasRef} style={{
           width: "21cm",
           minHeight: "29.7cm",
           display: "block",
           margin: "0 auto",
           padding: "1.5cm",
           marginBottom: ".5cm",
           boxShadow: "none",
           boxSizing: "border-box",
           position:'absolute',
           left: 'calc(50% - 400px)'
         }} /> */}

      <EditablePlugins
        // as={(props) => <div ref={canvasRef} {...props} />}
        className={className}
        style={{ fontSize: defaultFontSize }}
        plugins={plugins}
        readOnly={!isEditable || !editor.children.length}
        renderElementDeps={[doc, participants]}
        // decorate={[([node, path], editor) => {
        //   const ranges = [];

        //   if (!Text.isText(node)) {
        //       return ranges;
        //   }

        //   // const getLength = (token: any) => {
        //   //   if (typeof token === 'string') {
        //   //     return token.length;
        //   //   }
        //   //   if (typeof token.content === 'string') {
        //   //     return token.content.length;
        //   //   }
        //   //   return token.content.reduce((l: any, t: any) => l + getLength(t), 0);
        //   // };
        //   if (!node.text.length) {
        //     ranges.push({
        //         placeholder: true,
        //         anchor: { path, offset: 0 },
        //         focus: { path, offset: 12 },
        //     })

        //   }
        //   // console.log({node, text: node.text, data:  node.data })

        //   return ranges;
        // }]}
        // placeholder="Enter some rich text…"
        //  spellCheck
        autoFocus
        onKeyDown={[
          (event, editor) => {
            if (!editor.selection) {
              return;
            }

            const currentNode = editor.children[editor.selection.focus.path[0]];

            const hasText = Node.string(currentNode).length > 0;

            if (isSelectionAtBlockEnd(editor) && isHotkey("Delete", event)) {
              event.preventDefault();
              return false;
            }

            if (getListItemEntry(editor)) {
              return true;
            }

            if (currentNode.type === BLOCK_TABLE_WRAP) {
              return true;
            }

            // if (!isSelectionAtBlockEnd(editor) && isListItem(editor, editor.location) && isHotkey("mod+enter", event)) {
            //   return false;
            // }

            if (
              (isSelectionAtBlockStart(editor) || !hasText) &&
              isHotkey("backspace", event)
            ) {
              event.preventDefault();
              return false;
            }
          },
          onKeyDownVariable,
        ]}
        onKeyDownDeps={[path, search, target]}
      />

      <VariablesMenu
        at={target}
        valueIndex={path}
        variables={values}
        search={search}
        onVariableClick={onVariableClick}
        onAddVariable={onAddVariable}
      />
    </ErrorBoundary>
  );
}

export const DefaultBaseElement = ({ element, children }) => {
  return <div className="p-2">{children}</div>;
};
